// @custom — Analytics provider facade for first-party visitor counting.
//
// Provides getVisitorId / trackPageView / trackEvent that dispatch to the
// chosen provider (configured in config/@custom/analytics.js).
//
// Providers:
//   'local' (default) — keeps every captured page view in a local store
//                       (in-memory ring buffer + localStorage mirror) and
//                       never touches the network.
//   'api'   (opt-in)  — POSTs page views to the product's own server endpoint
//                       (/analytics/pageview). Also kept in the local store.
//   'none'            — no-op, nothing is captured and nothing is sent.
//   'ga4' | 'gtm' | 'plausible' — forward events to the provider's global
//                       (window.gtag / window.dataLayer.push /
//                       window.plausible) when the provider script is present;
//                       they load no scripts by themselves. Setting the
//                       provider and adding the script tag is the only step
//                       needed to enable a real provider (item 10).
//
// Every captured event has the canonical shape:
//   { event: 'page_view', path, timestamp, sessionId }
//
// When config/@custom/analytics.js debug is on, window.__analytics exposes
// getCapturedEvents() so all captured page views can be inspected (item 7).

import { analyticsConfig } from '@/config/@custom/analytics'

/** @typedef {Record<string, unknown>} AnalyticsParams */

/**
 * @typedef {object} AnalyticsEvent
 * @property {'page_view'} event
 * @property {string} path
 * @property {string} timestamp
 * @property {string} sessionId
 */

// ── Local store (in-memory array + localStorage ring buffer) ────────────────

/** Maximum number of page-view events kept in the local store. */
const MAX_CAPTURED_EVENTS = 100

/**
 * Type guard for the canonical analytics event shape.
 * @param {unknown} value
 * @returns {value is AnalyticsEvent}
 */
function isAnalyticsEvent(value) {
  if (!value || typeof value !== 'object') return false
  const candidate = /** @type {Record<string, unknown>} */ (value)
  return (
    candidate.event === 'page_view' &&
    typeof candidate.path === 'string' &&
    typeof candidate.timestamp === 'string' &&
    typeof candidate.sessionId === 'string'
  )
}

/**
 * Load captured events persisted in localStorage, or start empty.
 * @returns {AnalyticsEvent[]}
 */
function loadCapturedEvents() {
  try {
    if (typeof localStorage === 'undefined') return []
    const raw = localStorage.getItem(analyticsConfig.storageEventsKey)
    if (!raw) return []
    const parsed = /** @type {unknown} */ (JSON.parse(raw))
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isAnalyticsEvent)
  } catch {
    // Corrupt JSON or unavailable storage — fall back to an empty store.
    return []
  }
}

/** @type {AnalyticsEvent[]} */
let capturedEvents = loadCapturedEvents()

/**
 * Persist the current captured-event ring buffer to localStorage.
 */
function saveCapturedEvents() {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(analyticsConfig.storageEventsKey, JSON.stringify(capturedEvents))
  } catch {
    // localStorage may be unavailable (private mode, SSR) — keep in-memory only.
  }
}

/**
 * Append an event to the local store, capped at MAX_CAPTURED_EVENTS.
 * @param {AnalyticsEvent} event
 */
function recordEvent(event) {
  capturedEvents.push(event)
  if (capturedEvents.length > MAX_CAPTURED_EVENTS) {
    capturedEvents = capturedEvents.slice(-MAX_CAPTURED_EVENTS)
  }
  saveCapturedEvents()
  maybeAttachDebug()
}

// ── Debug / inspection ──────────────────────────────────────────────────────

/**
 * All captured page views (oldest first). Safe to call any time — also when
 * the debug flag is off; it is the mechanism window.__analytics uses too.
 * @returns {AnalyticsEvent[]}
 */
export function getCapturedEvents() {
  return capturedEvents.slice()
}

/**
 * When analyticsConfig.debug is on, expose the inspection helpers on
 * window.__analytics. Called after every capture so the debug surface is
 * available even when the flag is flipped after boot.
 */
function maybeAttachDebug() {
  if (!analyticsConfig.debug || typeof window === 'undefined') return
  const win = /** @type {{ __analytics?: unknown }} */ (window)
  if (win.__analytics) return
  win.__analytics = {
    config: analyticsConfig,
    getCapturedEvents,
    /** @returns {AnalyticsEvent[]} */
    get events() {
      return capturedEvents.slice()
    },
  }
}

// ── Provider interface ──────────────────────────────────────────────────────

/**
 * @typedef {object} AnalyticsProvider
 * @property {(path: string) => void | Promise<void>} trackPageView
 * @property {(eventName: string, params?: AnalyticsParams) => void | Promise<void>} trackEvent
 */

// ── 'none' provider (no-op) ─────────────────────────────────────────────────

/** @type {AnalyticsProvider} */
const noneProvider = {
  /** @param {string} _path */
  trackPageView(_path) {},
  /** @param {string} _eventName @param {AnalyticsParams} [_params] */
  trackEvent(_eventName, _params) {},
}

// ── 'local' provider (default — captures locally, sends nothing) ────────────

/** @type {AnalyticsProvider} */
const localProvider = {
  /** @param {string} path */
  trackPageView(path) {
    recordEvent(buildPageViewEvent(path))
  },
  /** @param {string} _eventName @param {AnalyticsParams} [_params] */
  trackEvent(_eventName, _params) {
    // Custom events are not part of the page-view ring buffer (reserved).
  },
}

// ── 'api' provider (opt-in first-party POST) ────────────────────────────────

/**
 * POST a page view to the product's analytics endpoint. The captured event is
 * also kept in the local store so the debug view can list it. This provider is
 * opt-in (config provider: 'api'); the default 'local' provider never touches
 * the network.
 * @param {string} path
 */
async function apiTrackPageView(path) {
  const event = buildPageViewEvent(path)
  recordEvent(event)
  const referrer = getReferrer()

  try {
    await fetch(analyticsConfig.pageviewEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: event.event,
        path: event.path,
        visitorId: event.sessionId,
        referrer,
        timestamp: event.timestamp,
      }),
    })
  } catch {
    // Silently ignore network errors — a failed page view must never break the page.
  }
}

/**
 * Stub for future custom event tracking with the 'api' provider.
 * @param {string} _eventName
 * @param {AnalyticsParams} [_params]
 */
function apiTrackEvent(_eventName, _params) {
  // Reserved for future use
}

/** @type {AnalyticsProvider} */
const apiProvider = {
  trackPageView: apiTrackPageView,
  trackEvent: apiTrackEvent,
}

// ── Provider globals (ga4 / gtm / plausible) ────────────────────────────────

/**
 * Read a provider global off window (window.gtag, window.dataLayer,
 * window.plausible) without tripping strict type checks — the globals only
 * exist after the provider script has been added.
 * @param {string} key
 * @returns {unknown}
 */
function globalOnWindow(key) {
  if (typeof window === 'undefined') return undefined
  return /** @type {Record<string, unknown>} */ (/** @type {unknown} */ (window))[key]
}

/**
 * The GA4 global (window.gtag), when the provider script added it.
 * @returns {((...args: unknown[]) => void) | undefined}
 */
function getGtag() {
  const gtag = globalOnWindow('gtag')
  return typeof gtag === 'function' ? /** @type {(...args: unknown[]) => void} */ (gtag) : undefined
}

/**
 * The Plausible global (window.plausible), when the provider script added it.
 * @returns {((event: string, opts?: Record<string, unknown>) => void) | undefined}
 */
function getPlausible() {
  const plausible = globalOnWindow('plausible')
  return typeof plausible === 'function'
    ? /** @type {(event: string, opts?: Record<string, unknown>) => void} */ (plausible)
    : undefined
}

/** @type {AnalyticsProvider} */
const ga4Provider = {
  /** @param {string} path */
  trackPageView(path) {
    recordEvent(buildPageViewEvent(path))
    const gtag = getGtag()
    if (gtag) gtag('event', 'page_view', { page_path: path })
  },
  /** @param {string} eventName @param {AnalyticsParams} [params] */
  trackEvent(eventName, params) {
    const gtag = getGtag()
    if (gtag) gtag('event', eventName, params ?? {})
  },
}

/** @type {AnalyticsProvider} */
const gtmProvider = {
  /** @param {string} path */
  trackPageView(path) {
    recordEvent(buildPageViewEvent(path))
    const dataLayer = globalOnWindow('dataLayer')
    if (Array.isArray(dataLayer) && typeof dataLayer.push === 'function') {
      /** @type {{ push: (item: unknown) => void }} */ (dataLayer).push({
        event: 'page_view',
        page_path: path,
      })
    }
  },
  /** @param {string} eventName @param {AnalyticsParams} [params] */
  trackEvent(eventName, params) {
    const dataLayer = globalOnWindow('dataLayer')
    if (Array.isArray(dataLayer) && typeof dataLayer.push === 'function') {
      /** @type {{ push: (item: unknown) => void }} */ (dataLayer).push({
        event: eventName,
        ...(params ?? {}),
      })
    }
  },
}

/** @type {AnalyticsProvider} */
const plausibleProvider = {
  /** @param {string} path */
  trackPageView(path) {
    recordEvent(buildPageViewEvent(path))
    const plausible = getPlausible()
    if (plausible) plausible('pageview', { u: path })
  },
  /** @param {string} eventName @param {AnalyticsParams} [params] */
  trackEvent(eventName, params) {
    const plausible = getPlausible()
    if (plausible) plausible(eventName, { props: params ?? {} })
  },
}

// ── Provider registry ───────────────────────────────────────────────────────

/** @type {Record<string, AnalyticsProvider>} */
const providers = {
  none: noneProvider,
  local: localProvider,
  api: apiProvider,
  ga4: ga4Provider,
  gtm: gtmProvider,
  plausible: plausibleProvider,
}

// ── Current provider (swap for testing) ─────────────────────────────────────

/** @type {AnalyticsProvider} */
let current = providers[analyticsConfig.provider] ?? localProvider

/**
 * Switch the active provider at runtime (primarily for tests).
 * @param {string} name
 */
export function setProvider(name) {
  current = providers[name] ?? noneProvider
}

/** @returns {AnalyticsProvider} */
export function getProvider() {
  return current
}

// ── Visitor id ──────────────────────────────────────────────────────────────

/**
 * Generate a random UUID. Uses crypto.randomUUID when available, falls back to
 * Math.random + Date.now for environments without it (older jsdom, etc.).
 * @returns {string}
 */
function generateId() {
  try {
    return crypto.randomUUID()
  } catch {
    // crypto.randomUUID may not be available in all jsdom versions
    return `${Math.random().toString(36).slice(2)}-${Date.now()}`
  }
}

/**
 * Read or create a stable visitor id persisted in localStorage.
 * Falls back to a random id when localStorage is unavailable.
 * @returns {string}
 */
export function getVisitorId() {
  try {
    let id = localStorage.getItem(analyticsConfig.storageKey)
    if (!id) {
      id = generateId()
      localStorage.setItem(analyticsConfig.storageKey, id)
    }
    return id
  } catch {
    return generateId()
  }
}

/**
 * Read the Referer header equivalent from document.referrer.
 * @returns {string | undefined}
 */
function getReferrer() {
  try {
    return document.referrer || undefined
  } catch {
    return undefined
  }
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Build the canonical page-view event for the current path and session.
 * @param {string} path
 * @returns {AnalyticsEvent}
 */
function buildPageViewEvent(path) {
  return {
    event: 'page_view',
    path,
    timestamp: new Date().toISOString(),
    sessionId: getVisitorId(),
  }
}

/**
 * Track a page view via the active provider. Honours the enabled toggle in
 * config/@custom/analytics.js (item 3).
 * @param {string} path
 */
export function trackPageView(path) {
  if (!analyticsConfig.enabled) return
  void current.trackPageView(path)
}

/**
 * Track a custom event via the active provider. Honours the enabled toggle in
 * config/@custom/analytics.js (item 3).
 * @param {string} eventName
 * @param {AnalyticsParams} [params]
 */
export function trackEvent(eventName, params) {
  if (!analyticsConfig.enabled) return
  void current.trackEvent(eventName, params)
}

// When debug mode is on from boot, expose the inspection surface immediately
// so it exists before the first page view is captured.
maybeAttachDebug()